import * as React from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';

import { cn } from '../lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'vai-flex vai-cursor-default vai-gap-2 vai-select-none vai-items-center vai-rounded-sm vai-px-2 vai-py-1.5 vai-text-sm vai-outline-none focus:vai-bg-accent data-[state=open]:vai-bg-accent [&_svg]:vai-pointer-events-none [&_svg]:vai-size-4 [&_svg]:vai-shrink-0',
      inset && 'vai-pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="vai-ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'vai-z-[10000] vai-min-w-[8rem] vai-overflow-hidden vai-rounded-md vai-border vai-bg-popover vai-p-1 vai-text-popover-foreground vai-shadow-lg data-[state=open]:vai-animate-in data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0 data-[state=closed]:vai-zoom-out-95 data-[state=open]:vai-zoom-in-95 data-[side=bottom]:vai-slide-in-from-top-2 data-[side=left]:vai-slide-in-from-right-2 data-[side=right]:vai-slide-in-from-left-2 data-[side=top]:vai-slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    zIndex?: number;
  }
>(({ className, sideOffset = 4, zIndex = 10000, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'vai-min-w-[8rem] vai-overflow-hidden vai-rounded-md vai-border vai-bg-popover vai-p-1 vai-text-popover-foreground vai-shadow-md data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0 data-[state=closed]:vai-zoom-out-95 data-[state=open]:vai-zoom-in-95 data-[side=bottom]:vai-slide-in-from-top-2 data-[side=left]:vai-slide-in-from-right-2 data-[side=right]:vai-slide-in-from-left-2 data-[side=top]:vai-slide-in-from-bottom-2',
        className,
        `vai-z-[${zIndex}]`,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'vai-relative vai-flex vai-cursor-default vai-select-none vai-items-center vai-gap-2 vai-rounded-sm vai-px-2 vai-py-1.5 vai-text-sm vai-outline-none vai-transition-colors focus:vai-bg-accent focus:vai-text-accent-foreground data-[disabled]:vai-pointer-events-none data-[disabled]:vai-opacity-50 [&_svg]:vai-pointer-events-none [&_svg]:vai-size-4 [&_svg]:vai-shrink-0',
      inset && 'vai-pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'vai-relative vai-flex vai-cursor-default vai-select-none vai-items-center vai-rounded-sm vai-py-1.5 vai-pl-8 vai-pr-2 vai-text-sm vai-outline-none vai-transition-colors focus:vai-bg-accent focus:vai-text-accent-foreground data-[disabled]:vai-pointer-events-none data-[disabled]:vai-opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="vai-absolute vai-left-2 vai-flex vai-h-3.5 vai-w-3.5 vai-items-center vai-justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="vai-h-4 vai-w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'vai-relative vai-flex vai-cursor-default vai-select-none vai-items-center vai-rounded-sm vai-py-1.5 vai-pl-8 vai-pr-2 vai-text-sm vai-outline-none vai-transition-colors focus:vai-bg-accent focus:vai-text-accent-foreground data-[disabled]:vai-pointer-events-none data-[disabled]:vai-opacity-50',
      className,
    )}
    {...props}
  >
    <span className="vai-absolute vai-left-2 vai-flex vai-h-3.5 vai-w-3.5 vai-items-center vai-justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="vai-h-2 vai-w-2 vai-fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'vai-px-2 vai-py-1.5 vai-text-sm vai-font-semibold',
      inset && 'vai-pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('vai--mx-1 vai-my-1 vai-h-px vai-bg-muted', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'vai-ml-auto vai-text-xs vai-tracking-widest vai-opacity-60',
        className,
      )}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
